import React from 'react';

import { Form, Button, Col, Row } from 'antd';

import {
  PlusOutlined,
  CloseCircleOutlined,
  CopyOutlined,
} from '@ant-design/icons';

import { FormAddButton, dialogFunction } from 'common/components';

import {
  HeaderGroupElement,
  GroupElementWrapper,
  FormItemList,
  FormItemContainer,
  LabelFormItem,
  FormItemText,
  FormItemNumber,
  FormItemBoolean,
  FormItemSelectSpecialEnum,
  FormItemSelectEnum,
  FormItemDatetime,
  FormItemAllergenBoolean,
  FormItemAllergenSelectEnum,
  FormItemTextArea,
} from '../../shared/components';

import FormSingleNestedValue from './FormSingleNestedValue';

import {
  useProductDetail,
  useProductDetailDispatch,
  productDetailActionTypes,
} from './ProductDetailContext';

import {
  getIdxCopiedField,
  findProductPropertyEnums,
  checkTypeOfFormNested,
  getFormNameItem,
  generateNestedIdxFormElement,
  getFieldValueFromListIdx,
  checkIsValidObjectValues,
  checkDisabledDeleteFormField,
} from './utils';

import { NUMERIC_TYPE } from 'static/Constants';
import {
  SPECIAL_ENUMS,
  LIST_BOOLEAN_PROPERTY_NAME_NEED_TO_DISABLE,
  LIST_PROPERTY_NAME_NEED_TO_DISABLE_COPY_ACTION,
  LIST_ALLERGEN_PROPERTY_NAME_CHECKABLE,
  LIST_PROPERTY_TYPE_TEXTAREA,
} from 'pages/product-full-view/constants';

import { useCallbackRef } from 'hooks';

import {
  generateOriginalNestedIdxFormElement,
  generateOriginalStringPath,
} from './overlay/utils';

const FormMultipleNestedValue = ({
  properties,
  name,
  label,
  isArray,
  property,
  parentNestedIndex = [],
  productEnums = [],
  productModules,
  handleValidate,
  deleteFieldForm,
  ...otherProps
}) => {
  const { formInstance } = useProductDetail();
  const dispatch = useProductDetailDispatch();

  const { propertyName: parentPropertyName } = property;

  const renderItem = ({ property, indexField, parentNestedIndex }) => {
    const {
      propertyType,
      propertyName,
      propertyDisplayName,
      fieldFullPath,
      isArray,
      childProperties,
      listName,
    } = property;

    const nameFormItem = getFormNameItem(indexField, propertyName);

    const { isSingleNestedValue, isMultipleNestedValue } =
      checkTypeOfFormNested(childProperties, isArray);

    if (isSingleNestedValue) {
      const singleParentNestedIndex = parentNestedIndex.concat(propertyName);

      return (
        <FormSingleNestedValue
          productModules={productModules}
          name={nameFormItem}
          property={property}
          productEnums={productEnums}
          parentNestedIndex={singleParentNestedIndex}
          handleValidate={handleValidate}
          deleteFieldForm={deleteFieldForm}
        />
      );
    }

    if (isMultipleNestedValue) {
      return (
        <FormMultipleNestedValue
          properties={childProperties}
          name={nameFormItem}
          isArray={isArray}
          label={propertyDisplayName}
          property={property}
          parentNestedIndex={parentNestedIndex}
          productEnums={productEnums}
          handleValidate={handleValidate}
          deleteFieldForm={deleteFieldForm}
          productModules={productModules}
        />
      );
    }

    if (listName) {
      const propertyEnums = findProductPropertyEnums(listName, productEnums);

      if (SPECIAL_ENUMS.includes(listName)) {
        return (
          <FormItemSelectSpecialEnum
            property={property}
            nameFormItem={nameFormItem}
            propertyEnums={propertyEnums}
            labelCol={{ flex: '300px' }}
          />
        );
      }

      if (LIST_ALLERGEN_PROPERTY_NAME_CHECKABLE.includes(fieldFullPath)) {
        return (
          <FormItemAllergenSelectEnum
            labelCol={{ flex: '300px' }}
            property={property}
            nameFormItem={nameFormItem}
            propertyEnums={propertyEnums}
            parentNestedIndex={parentNestedIndex}
          />
        );
      }
      return (
        <FormItemSelectEnum
          property={property}
          nameFormItem={nameFormItem}
          propertyEnums={propertyEnums}
          labelCol={{ flex: '300px' }}
        />
      );
    }

    if (isArray) {
      if (NUMERIC_TYPE.includes(property.propertyType)) {
        return (
          <FormItemList
            productModules={productModules}
            property={property}
            name={nameFormItem}
            className='product-detail__form-list'
            variant='number'
            labelCol={{ flex: '300px' }}
            spacerColProps={{ flex: '0 0 300px' }}
            errorsColProps={{ flex: 'auto', style: { marginLeft: 300 } }}
          />
        );
      } else if (property.propertyType === 'datetime') {
        return (
          <FormItemList
            productModules={productModules}
            property={property}
            name={nameFormItem}
            className='product-detail__form-list'
            variant='datetime'
            labelCol={{ flex: '300px' }}
            spacerColProps={{ flex: '0 0 300px' }}
            errorsColProps={{ flex: 'auto', style: { marginLeft: 300 } }}
          />
        );
      } else {
        return (
          <FormItemList
            productModules={productModules}
            property={property}
            name={nameFormItem}
            className='product-detail__form-list'
            variant='text'
            labelCol={{ flex: '300px' }}
            spacerColProps={{ flex: '0 0 300px' }}
            errorsColProps={{ flex: 'auto', style: { marginLeft: 300 } }}
          />
        );
      }
    }

    if (propertyType === 'string') {
      const textareaProperty =
        LIST_PROPERTY_TYPE_TEXTAREA[fieldFullPath?.toLowerCase()];
      if (textareaProperty) {
        return (
          <FormItemTextArea
            property={property}
            name={nameFormItem}
            style={{ marginBottom: '18px' }}
            {...textareaProperty}
          />
        );
      } else
        return (
          <FormItemText
            property={property}
            name={nameFormItem}
            labelCol={{ flex: '300px' }}
          />
        );
    }

    if (NUMERIC_TYPE.includes(propertyType)) {
      return (
        <FormItemNumber
          name={nameFormItem}
          property={property}
          labelCol={{ flex: '300px' }}
        />
      );
    }

    if (propertyType === 'datetime') {
      return (
        <FormItemDatetime
          name={nameFormItem}
          property={property}
          labelCol={{ flex: '300px' }}
        />
      );
    }

    if (propertyType === 'boolean') {
      const shouldCheckDisabled =
        LIST_BOOLEAN_PROPERTY_NAME_NEED_TO_DISABLE.includes(fieldFullPath);

      if (shouldCheckDisabled) {
        return (
          <FormItemAllergenBoolean
            parentNestedIndex={parentNestedIndex}
            name={nameFormItem}
            property={property}
            labelCol={{ flex: '300px' }}
          />
        );
      }

      return (
        <FormItemBoolean
          name={nameFormItem}
          property={property}
          labelCol={{ flex: '300px' }}
          parentNestedIndex={parentNestedIndex}
        />
      );
    }

    // Default display text input for catching bug from incomplete BE data
    return (
      <FormItemText
        property={property}
        name={nameFormItem}
        labelCol={{ flex: '300px' }}
      />
    );
  };

  const handleUpdateDeletedFieldsOverlay = (indexesField) => {
    const originalFieldFormValues = getFieldValueFromListIdx(
      productModules,
      indexesField
    );

    if (
      originalFieldFormValues === null ||
      originalFieldFormValues === undefined
    ) {
      return;
    }

    dispatch({
      type: productDetailActionTypes.UPDATE_DELETED_FIELDS_OVERLAY,
      pathField: generateOriginalStringPath(indexesField),
    });
  };

  const handleDeleteFieldForm = useCallbackRef(
    ({ idxFormElement, idxOriginalFormElement, field, removeCallback }) => {
      const formValues = formInstance.getFieldsValue();
      const fieldFormValues = getFieldValueFromListIdx(
        formValues,
        idxFormElement
      );
      const isValidValues = checkIsValidObjectValues(fieldFormValues);

      // Only show confirm modal when field has any data
      if (isValidValues) {
        dialogFunction({
          type: 'warn',
          content: 'Are you sure you want to delete selected property?',
          okText: 'Delete',
          okButtonProps: {
            type: 'danger',
          },
          onOk: () => {
            removeCallback(field.name);
            handleValidate(formValues);
            handleUpdateDeletedFieldsOverlay(idxOriginalFormElement);
            deleteFieldForm({ fieldPath: idxFormElement, fieldFormValues });
          },
        });
      } else {
        removeCallback(field.name);
        handleUpdateDeletedFieldsOverlay(idxOriginalFormElement);
      }
    }
  );

  return (
    <Form.List name={name} {...otherProps}>
      {(fields, { add, remove }, { errors }) => {
        return (
          <>
            {fields.map((field, index) => {
              const concatenatedParentNestedIndex =
                generateNestedIdxFormElement({
                  parentNestedIndex,
                  propertyName: parentPropertyName,
                  field,
                });

              return (
                <Row
                  style={{
                    marginTop: 20,
                    position: 'relative',
                  }}
                  wrap={false}
                  key={field.name}
                >
                  <GroupElementWrapper
                    flex='auto'
                    style={{ marginLeft: 24, marginBottom: 8 }}
                    className='product-detail__group-element'
                  >
                    <HeaderGroupElement
                      header={`${label} ${index + 1}`}
                      className='product-detail__group-header'
                    />

                    {properties.map((property) => {
                      if (property?.childProperties?.length > 0) {
                        return (
                          <FormItemContainer
                            {...field}
                            key={[field.name, property.propertyName]}
                            fieldKey={[field.fieldKey, property.propertyName]}
                          >
                            {renderItem({
                              property,
                              indexField: [field.name],
                              parentNestedIndex: concatenatedParentNestedIndex,
                            })}
                          </FormItemContainer>
                        );
                      }

                      return (
                        <React.Fragment
                          key={[field.name, property.propertyName]}
                        >
                          {renderItem({
                            property,
                            indexField: [field.name],
                            parentNestedIndex: concatenatedParentNestedIndex,
                          })}
                        </React.Fragment>
                      );
                    })}
                  </GroupElementWrapper>

                  {fields.length > 0 ? (
                    <Col
                      flex='0 0 auto'
                      style={{
                        marginLeft: 4,
                        marginRight: 4,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Button
                        type='danger'
                        onClick={() => {
                          handleDeleteFieldForm({
                            idxFormElement: concatenatedParentNestedIndex,
                            idxOriginalFormElement:
                              generateOriginalNestedIdxFormElement({
                                parentNestedIndex,
                                propertyName: parentPropertyName,
                                field,
                              }),
                            field,
                            removeCallback: remove,
                          });
                        }}
                        icon={<CloseCircleOutlined />}
                        className='product-detail__btn-delete'
                        disabled={checkDisabledDeleteFormField({
                          totalFields: fields.length,
                          fieldFullPath: property.fieldFullPath,
                        })}
                      />

                      <Button
                        type='primary'
                        className='product-detail__btn-copy'
                        icon={<CopyOutlined />}
                        disabled={LIST_PROPERTY_NAME_NEED_TO_DISABLE_COPY_ACTION.includes(
                          property.propertyName
                        )}
                        onClick={() => {
                          const idxCopyField = getIdxCopiedField({
                            parentNestedIndex,
                            parentPropertyName,
                            field,
                          });
                          dispatch({
                            type: productDetailActionTypes.COPY_FIELD_FORM,
                            payload: {
                              formInstance,
                              idxCopyField,
                            },
                          });
                        }}
                      />
                    </Col>
                  ) : null}
                </Row>
              );
            })}

            <Row className='product-detail__multiple-form-container'>
              <Col
                flex='0 0 300px'
                style={{
                  padding: '4px 0px',
                  textAlign: 'right',
                }}
                className='product-detail__label-form'
              >
                <LabelFormItem
                  label={property.propertyDisplayName}
                  tooltip={property.propertyDescription}
                  fieldFullPath={property.fieldFullPath}
                />
              </Col>

              <FormAddButton
                type='secondary'
                onClick={() => add()}
                icon={<PlusOutlined />}
                disabled={isArray ? false : fields.length > 0}
                text='Add value'
              />
            </Row>

            {errors.length > 0 && (
              <Row>
                <Col flex='0 0 300px' />
                <Col flex='auto'>{errors[0]}</Col>
              </Row>
            )}
          </>
        );
      }}
    </Form.List>
  );
};
export default FormMultipleNestedValue;
